<template>
  <div class="custom-filter-editor"
       :class="filterEditorStyles">
    <custom-filter-editor-name :filter-name="name"
                               @changeName="changeName" />

    <div class="about-info" @click="sendAnalyticsEvent">
      {{ $t("customContentFilter.title") }}

      <div class="info-icon">?</div>
    </div>

    <custom-filter-editor-input :add-icon-disabled="nameExists"
                                @addKeyword="addKeyword"
                                @focus="$emit('focus')"
                                @blur="$emit('blur')" />

    <custom-filter-editor-list :keywords="editorKeywords"
                               @deleteKeyword="deleteKeyword" />
  </div>
</template>

<script>
  import CustomFilterEditorName  from '@/components/Child/ChildCustomFilter/CustomFilterEditorName'
  import CustomFilterEditorInput from '@/components/Child/ChildCustomFilter/CustomFilterEditorInput'
  import CustomFilterEditorList  from '@/components/Child/ChildCustomFilter/CustomFilterEditorList'

  import contentFiltersApi from '@/api/contentFilters'
  import errorNotification from '@/mixins/errorNotification.js'
  import analytics from '@/services/analytics.js'

  export default {
    mixins: [
      errorNotification
    ],

    components: {
      CustomFilterEditorName,
      CustomFilterEditorInput,
      CustomFilterEditorList
    },

    props: {
      mode: {
        type: String,
        required: false,
        default: 'create'
      },

      modeId: {
        type: String,
        required: true
      },

      childId: {
        type: String,
        required: true
      },

      name: {
        type: String,
        required: false,
        default: ''
      },

      filterId: {
        type: Number,
        required: false
      },

      keywords: {
        type: Array,
        required: false,
        default: () => []
      }
    },

    data() {
      return {
        editorName: this.name,
        editorKeywords: this.keywords,
      }
    },

    computed: {
      ...mapGetters('auth', {
        currentUser: 'currentUser'
      }),

      groupId() {
        return this.currentUser.group_id
      },

      filterEditorStyles() {
        return {
          'custom-filter-editor-mobile': this.$mq === 'mobile'
        }
      },

      nameExists() {
        return !this.editorName.trim().length
      }
    },

    methods: {
      changeName(name) {
        this.editorName = name
      },

      keywordExists(keyword) {
        if (Array.isArray(keyword)) {
          const phrases = this.editorKeywords.filter(item => Array.isArray(item))

          return phrases.find(phrase => {
            return phrase.join(' ').toLowerCase() === keyword.join(' ').toLowerCase()
          })
        }

        const keywords = this.editorKeywords.filter(item => !Array.isArray(item))

        return keywords.find(item => {
          return item.toLowerCase() === keyword.toLowerCase()
        })
      },

      addKeyword(keyword) {
        if (this.keywordExists(keyword)) {
          return
        }

        this.editorKeywords.unshift(keyword)
      },

      deleteKeyword(index) {
        this.editorKeywords.splice(index, 1)
      },

      openFiltersList() {
        if (this.$mq === 'mobile') {
          this.$router.push({
            name: 'children.single.customFilters',
            params: {
              modeId: this.modeId,
              childId: this.childId
            }
          })

          return
        }

        this.showModal('customFilters', {
          modeId: this.modeId,
          childId: this.childId
        })
      },

      createFilter() {
        return this.$validator.validate('filter-name')
          .then(valid => {
            if (!valid) {
              return
            }

            return contentFiltersApi.addCustomFilter(
              this.groupId,
              this.editorName,
              this.keywords
            )
              .then(() => {
                this.openFiltersList()
              })
              .catch(() => {
                this.failedRequestNotification()
              })
          })
      },

      updateFilter() {
        return this.$validator.validate('filter-name')
          .then(valid => {
            if (!valid) {
              return
            }

            return contentFiltersApi.upadateCustomFilter(
              this.filterId,
              this.groupId,
              this.editorName,
              this.keywords
            )
              .then(() => {
                this.openFiltersList()
              })
              .catch(() => {
                this.failedRequestNotification()
              })
          })
      },

      removeFilter() {
        return contentFiltersApi.deleteCustomFilter(
          this.filterId,
          this.groupId
        )
          .then(() => {
            this.openFiltersList()
          })
          .catch(() => {
            this.failedRequestNotification()
          })
      },

      sendAnalyticsEvent() {
        analytics.sendEvent('CustmContnFltrs_info')
      }
    }
  }
</script>

<style lang="scss" scoped>
  .custom-filter-editor {
    width: 100%;
    height: 100%;
    min-height: 400px;

    padding: 0;
    margin: 0;

    display: flex;
    flex-direction: column;

    user-select: none;

    .about-info {
      display: flex;
      justify-content: center;

      margin: 5px 0;
      cursor: pointer;
      color: map-get($colors, primary);

      .info-icon {
        display: flex;
        justify-content: center;
        align-items: center;

        width: 18px;
        height: 18px;
        font-weight: bold;
        border: 1px solid;
        border-radius: 50%;

        margin: 0 5px;
        padding-top: 3px;
      }
    }
  }

  .custom-filter-editor-mobile {
    min-height: 0;
  }
</style>